"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Clock, Cpu, Users } from "lucide-react";
import { cn } from "@/lib/utils";
interface StatItem {
  id: string;
  icon: typeof Cpu;
  numberVi: string;
  numberEn: string;
  subtextVi: string;
  subtextEn: string;
}
const STATS_DATA: StatItem[] = [{
  id: "modules",
  icon: Cpu,
  numberVi: "10 HỌC PHẦN ROBOTICS CỐT LÕI",
  numberEn: "10 CORE ROBOTICS MODULES",
  subtextVi: "D1 → D4 + HP1 → HP6",
  subtextEn: "D1 → D4 + HP1 → HP6"
}, {
  id: "age-range",
  icon: Users,
  numberVi: "4–13+ TUỔI",
  numberEn: "AGES 4–13+",
  subtextVi: "Tùy theo từng giai đoạn và điều kiện đầu vào của khóa học",
  subtextEn: "Varies by stage and course entry requirements"
}, {
  id: "sessions",
  icon: BookOpen,
  numberVi: "120 BUỔI HỌC",
  numberEn: "120 SESSIONS",
  subtextVi: "10 học phần × 12 buổi",
  subtextEn: "10 modules × 12 sessions"
}, {
  id: "hours",
  icon: Clock,
  numberVi: "180 GIỜ HỌC",
  numberEn: "180 HOURS",
  subtextVi: "Tổng thời lượng theo cấu trúc 10 học phần × 12 buổi × 90 phút",
  subtextEn: "Total duration based on 10 modules × 12 sessions × 90 minutes"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 12
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: "easeOut"
    }
  }
};
export default function QuickFacts() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const storedLang = localStorage.getItem("roboto_lang") as "vi" | "en" | null;
    if (storedLang === "vi" || storedLang === "en") {
      setLang(storedLang);
    }
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent<"vi" | "en">;
      if (customEvent?.detail) {
        setLang(customEvent.detail);
      } else {
        const updated = localStorage.getItem("roboto_lang") as "vi" | "en" | null;
        if (updated === "vi" || updated === "en") {
          setLang(updated);
        }
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  const borderClasses = ["border-b sm:border-r sm:border-b border-border", "border-b border-border", "border-b sm:border-b-0 sm:border-r border-border", ""];
  return <section id="quickfactsstatbar" data-nav="light" className="bg-background text-foreground border-y border-border py-8 md:py-12 relative overflow-hidden">
      {/* Background subtle technical grid accent */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
          {/* Quick Facts Stats Grid */}
          <motion.div className="lg:col-span-7 xl:col-span-8 grid grid-cols-1 sm:grid-cols-2 rounded-xl bg-card border border-border shadow-xs overflow-hidden" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }}>
            {STATS_DATA.map((item, index) => {
            const Icon = item.icon;
            const title = lang === "vi" ? item.numberVi : item.numberEn;
            const subtext = lang === "vi" ? item.subtextVi : item.subtextEn;
            return <motion.div key={item.id} data-index={index} variants={itemVariants} className={cn("p-5 sm:p-6 md:p-8 flex flex-col justify-between transition-colors hover:bg-muted/30 group", borderClasses[index])}>
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 rounded-md bg-muted text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                        <Icon className="size-4 sm:size-5 shrink-0" />
                      </div>
                    </div>

                    <h3 className="text-base sm:text-lg lg:text-xl font-bold tracking-tight text-primary font-sans leading-snug uppercase">
                      {title}
                    </h3>
                  </div>

                  <p className="mt-3 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {subtext}
                  </p>
                </motion.div>;
          })}
          </motion.div>

          {/* Supporting Photography Frame */}
          <motion.div className="lg:col-span-5 xl:col-span-4 relative rounded-xl overflow-hidden border border-border bg-card shadow-xs min-h-[260px] sm:min-h-[300px] flex flex-col justify-end group" initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_lab_classroom.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_lab_classroom.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_lab_classroom.png-wvc-sizes" alt="Học sinh thực hành tại phòng Lab Robotics ROBOTO" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            {/* Tech Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/40 to-transparent" />

            {/* Spec Rail Top Accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-secondary" />

            {/* Tech spec tag / caption badge */}
            <div className="relative z-10 p-5 sm:p-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/20 text-secondary border border-secondary/40 text-xs font-bold tracking-wider uppercase mb-2">
                <span className="size-1.5 rounded-lg bg-secondary animate-pulse" />
                {lang === "vi" ? "Phòng Lab Đạt Chuẩn" : "Standard Lab Setup"}
              </div>
              <p className="text-xs sm:text-sm font-medium text-foreground/90 leading-snug">
                {lang === "vi" ? "Môi trường học tập tiêu chuẩn với trang thiết bị Robotics hiện đại." : "Standard learning environment equipped with modern Robotics technology."}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}